import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Play, ExternalLink, Disc } from "lucide-react";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { UserMusicService } from "../services/UserMusicServices";

export default function Music() {
    const [albums, setAlbums] = useState([]);
    const [settings, setSettings] = useState({
        spotify_url: "",
        apple_music_url: "",
        soundcloud_url: "",
        audiomack_url: ""
    });
    const [loading, setLoading] = useState(true);
    const [isSettingsLoading, setIsSettingsLoading] = useState(true);
    const [, setError] = useState(null);

    useEffect(() => {
        fetchMusic();
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setIsSettingsLoading(true);
            const response = await UserMusicService.getMusicSettings();
            if (response.status === "success") {
                setSettings(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch music settings:", error);
        } finally {
            setIsSettingsLoading(false);
        }
    };

    const fetchMusic = async () => {
        try {
            const data = await UserMusicService.getAllMusic();
            setAlbums(data);
        } catch (error) {
            console.error("Failed to fetch music:", error);
            setError("Unable to load the discography at this time.");
        } finally {
            setLoading(false);
        }
    };

    const streamingPlatforms = [
        { name: "Spotify", url: settings.spotify_url, color: "hover:text-[#1DB954]" },
        { name: "Apple Music", url: settings.apple_music_url, color: "hover:text-[#FA243C]" },
        { name: "SoundCloud", url: settings.soundcloud_url, color: "hover:text-[#FF5500]" },
        { name: "Audiomack", url: settings.audiomack_url, color: "hover:text-[#629AA9]" },
    ].filter(platform => platform.url && platform.url !== "" && platform.url !== "#" && platform.url !== "null");

    // Skeleton Component
    const SkeletonCard = () => (
        <div className="bg-white border border-black/10 overflow-hidden animate-pulse shadow-sm">
            <div className="aspect-square bg-gray-200" />
            <div className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                    <div className="h-6 bg-gray-200 w-2/3 rounded" />
                    <div className="h-4 bg-gray-200 w-12 rounded" />
                </div>
                <div className="space-y-2">
                    <div className="h-3 bg-gray-200 w-full rounded" />
                    <div className="h-3 bg-gray-200 w-5/6 rounded" />
                </div>
                <div className="flex gap-4 pt-2">
                    <div className="h-10 bg-gray-200 flex-1 rounded" />
                    <div className="h-10 bg-gray-200 flex-1 rounded" />
                </div>
            </div>
        </div>
    );

    const PlatformSkeleton = () => (
        <div className="flex flex-wrap justify-center gap-8 sm:gap-12">
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-6 w-24 bg-gray-200/60 animate-pulse rounded-full" />
            ))}
        </div>
    );

    const [activeCategory, setActiveCategory] = useState("ALL");

    const categories = ["ALL", "Band Leader", "Sideman", "Producer"];
    const filteredAlbums = activeCategory === "ALL"
        ? albums
        : albums.filter(album => (album.category || album.type || "").toLowerCase() === activeCategory.toLowerCase());

    return (
        <div className="min-h-screen bg-white text-black">
            <SEO title="Discography & Music Releases" description="Explore Temidayo Balogun's full discography, albums, and streaming releases." url="/discography" />
            <JazzHeader />

            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">

                    {/* Hero / Title Section */}
                    <div className="mb-12 sm:mb-16 text-center">
                        <h1
                            className="text-4xl sm:text-5xl lg:text-7xl font-normal mb-6"
                            style={{ fontFamily: "Playfair Display, Georgia, serif" }}
                        >
                            Discography
                        </h1>
                        <div className="w-24 h-[3px] bg-jazz-brown mx-auto mb-8" />
                        <p
                            className="max-w-2xl mx-auto text-black/70 text-lg leading-relaxed mb-8"
                            style={{ fontFamily: "Crimson Pro, Georgia, serif" }}
                        >
                            A chronological journey through sound, rhythm, and culture.
                        </p>

                        {/* Category Filter Tabs */}
                        <div className="flex flex-wrap justify-center gap-3">
                            {categories.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    className={`px-5 py-2 text-xs font-bold tracking-widest uppercase rounded-full border transition-all duration-300 ${
                                        activeCategory === cat
                                            ? "bg-jazz-brown text-white border-jazz-brown shadow-md"
                                            : "bg-white text-black/70 border-black/10 hover:border-jazz-brown hover:text-black"
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Album Grid & Platforms */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-32 min-h-[600px]">
                            {Array(6).fill(0).map((_, i) => <SkeletonCard key={i} />)}
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-32">
                                {filteredAlbums.length > 0 ? (
                                    filteredAlbums.map((album, index) => (
                                        <article
                                            key={album.id || index}
                                            className="group relative bg-white border border-black/10 overflow-hidden hover:shadow-[0_20px_40px_rgba(0,0,0,0.11)] hover:-translate-y-1 transition-all duration-500"
                                        >
                                            {/* Image */}
                                            <div className="relative aspect-square overflow-hidden bg-gray-50">
                                                <img
                                                    src={album.cover_path}
                                                    alt={album.title}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                                />

                                                {/* Overlay Type Badge */}
                                                <div className="absolute top-4 right-4 bg-white text-black px-3 py-1 text-xs font-bold tracking-widest uppercase shadow-sm">
                                                    {album.category || album.type}
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="p-6">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h3
                                                        className="text-xl font-medium text-black group-hover:text-jazz-brown transition-colors duration-300"
                                                        style={{ fontFamily: "Playfair Display, serif" }}
                                                    >
                                                        {album.feature_artist
                                                            ? `${album.feature_artist} - ${album.title}`
                                                            : album.title}
                                                    </h3>
                                                    <span className="text-black/90 text-sm font-mono font-bold mt-1">{album.year}</span>
                                                </div>

                                                <p
                                                    className="text-black text-sm font-medium leading-relaxed mb-6 line-clamp-2 h-10"
                                                    style={{ fontFamily: "Crimson Pro, serif" }}
                                                >
                                                    {album.description}
                                                </p>

                                                <div className="flex gap-4 border-t border-black/10 pt-4">
                                                    <Link
                                                        to={album.spotify_url || album.apple_music_url || "#"}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex-1 bg-black text-white py-2.5 text-xs font-bold tracking-wider hover:bg-jazz-brown transition-colors duration-300 flex items-center justify-center gap-2"
                                                    >
                                                        <Play size={14} fill="currentColor" /> LISTEN
                                                    </Link>
                                                    <Link
                                                        to={`/discography/${album.id}`}
                                                        className="flex-1 border border-black/20 text-black py-2.5 text-xs font-bold tracking-wider hover:border-jazz-brown hover:text-jazz-brown transition-colors duration-300 flex items-center justify-center gap-2"
                                                    >
                                                        DETAILS <ExternalLink size={14} />
                                                    </Link>
                                                </div>
                                            </div>
                                        </article>
                                    ))
                                ) : (
                                    <div className="col-span-full py-32 text-center border border-dashed border-black/20 bg-gray-100/30 rounded-2xl">
                                        <Disc size={48} className="mx-auto text-black/40 mb-6 animate-pulse" />
                                        <h3 className="text-2xl font-serif text-black mb-2" style={{ fontFamily: "Playfair Display, serif" }}>No releases found</h3>
                                        <p className="text-sm text-black font-bold tracking-widest uppercase">The discography is being updated. Please check back later.</p>
                                    </div>
                                )}
                            </div>

                            {/* Streaming Platforms Section */}
                            {(isSettingsLoading || settings) && (
                                <div className="max-w-4xl mx-auto text-center border-t border-black/10 pt-20">
                                    <h2
                                        className="text-2xl sm:text-3xl font-light mb-8"
                                        style={{ fontFamily: "Playfair Display, serif" }}
                                    >
                                        Listen on your favorite platform
                                    </h2>

                                    {isSettingsLoading ? (
                                        <PlatformSkeleton />
                                    ) : streamingPlatforms.length > 0 ? (
                                        <div className="flex flex-wrap justify-center gap-8 sm:gap-12 animate-in fade-in duration-700">
                                            {streamingPlatforms.map((platform) => (
                                                <a
                                                    key={platform.name}
                                                    href={platform.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`text-black/80 text-base sm:text-lg font-medium transition-colors duration-300 ${platform.color} flex items-center gap-2`}
                                                    style={{ fontFamily: "Montserrat, sans-serif" }}
                                                >
                                                    {platform.name}
                                                </a>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-black/40 text-sm font-medium italic animate-in fade-in duration-1000">
                                            Stay tuned! More streaming platforms coming soon.
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                </div>
            </main>

            <JazzFooter />
        </div>
    );
}
